import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";
import { fetchEscopoIdsVendedor } from "@/lib/vendedorScope";
import { findContratoDaCadeiaPedido } from "@/lib/contratoRenovacao";

export interface Contrato {
  id: number;
  cliente_id: number;
  projeto_id?: number;
  pedido_id?: number;
  numero_contrato: string;
  valor: number;
  status: string;
  data_inicio: string;
  data_fim?: string;
  observacoes?: string;
  arquivo_pdf_url?: string;
  arquivo_pdf_name?: string;
  seguro_ativado?: boolean;
  data_ativacao_seguro?: string;
  primeira_parcela_paga?: boolean;
  data_primeira_parcela_paga?: string;
  numero_ciclo?: number | null;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cidade?: string;
    estado?: string;
  };
  projeto?: {
    id: number;
    status: string;
  };
  pedido?: {
    id: number;
    numero_pedido: string;
    status: string;
  };
}

export type ContratosFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // contratos.data_inicio
  status?: string | null;
  valorMin?: number | null;
  valorMax?: number | null;
  estado?: string | null; // via cliente
  cidade?: string | null; // via cliente
} | null;

// Hook para contar total de contratos
export function useContratosCount() {
  const { user, profile, loading } = useAuth();
  const isScoped = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['contratos', 'count', isScoped ? user?.id : null],
    queryFn: async () => {
      if (isScoped) {
        const { pedidoIds } = await fetchEscopoIdsVendedor(user!.id);
        if (pedidoIds.length === 0) return 0;
        const { count, error } = await supabase
          .from('contratos')
          .select('*', { count: 'exact', head: true })
          .in('pedido_id', pedidoIds);
        if (error) throw error;
        return count || 0;
      }

      const { count, error } = await supabase
        .from('contratos')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Erro ao contar contratos:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !loading,
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar contratos paginados
export function useContratosPaginated(page: number = 1, limit: number = 15, searchTerm?: string, filtros?: ContratosFiltroParams) {
  const { user, profile, loading } = useAuth();
  const isScoped = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['contratos', 'paginated', page, limit, searchTerm, filtros ?? null, isScoped ? user?.id : null],
    queryFn: async () => {
      console.log(`🔍 Buscando contratos - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        let escopoPedidoIds: number[] | null = null;
        if (isScoped) {
          const { pedidoIds } = await fetchEscopoIdsVendedor(user!.id);
          if (pedidoIds.length === 0) return { data: [] as Contrato[], count: 0 };
          escopoPedidoIds = pedidoIds;
        }

        const clienteFilterPresent = !!(
          (filtros?.estado && filtros.estado.trim()) ||
          (filtros?.cidade && filtros.cidade.trim())
        );

        // Construir query base
        let query = supabase
          .from('contratos')
          .select(`
            *,
            cliente:clientes${clienteFilterPresent ? '!inner' : ''}(id, nome, email, telefone, cidade, estado),
            pedido:pedidos!pedido_id(id, numero_pedido, status),
            projeto:projetos!projeto_id(id, status)
          `, { count: 'exact' });

        if (escopoPedidoIds) {
          query = query.in('pedido_id', escopoPedidoIds);
        }

        // Filtros
        if (filtros?.periodo?.dataInicio) query = query.gte("data_inicio", filtros.periodo.dataInicio);
        if (filtros?.periodo?.dataFim) query = query.lte("data_inicio", filtros.periodo.dataFim);
        if (filtros?.status && filtros.status.trim()) query = query.eq("status", filtros.status.trim());
        if (typeof filtros?.valorMin === "number") query = query.gte("valor", filtros.valorMin);
        if (typeof filtros?.valorMax === "number") query = query.lte("valor", filtros.valorMax);
        if (filtros?.estado && filtros.estado.trim()) query = query.eq("cliente.estado", filtros.estado.trim().toUpperCase());
        if (filtros?.cidade && filtros.cidade.trim()) query = query.eq("cliente.cidade", filtros.cidade.trim());

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim().toLowerCase();
          
          // Se for número, buscar por ID do contrato
          if (!isNaN(Number(searchTerm.trim()))) {
            const idSearch = parseInt(searchTerm.trim());
            query = query.eq('id', idSearch);
          } else {
            // Se for texto, buscar em campos diretos e também em clientes relacionados
            const { data: clientesEncontrados } = await supabase
              .from('clientes')
              .select('id')
              .ilike('nome', `%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.or(`numero_contrato.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(',')})`);
            } else {
              query = query.or(`numero_contrato.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
            }
          }
        }

        // Ordenar e paginar
        query = query.order('id', { ascending: false }).range(from, to);

        const { data: contratos, error: errorContratos, count } = await query;

        if (errorContratos) {
          console.error('❌ Erro ao buscar contratos:', errorContratos);
          throw errorContratos;
        }

        console.log(`✅ Contratos encontrados na página ${page}:`, contratos?.length || 0, `Total: ${count || 0}`);
        return { data: ((contratos || []) as unknown) as Contrato[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de contratos:', error);
        throw error;
      }
    },
    enabled: !loading,
    staleTime: 30 * 1000, // 30 segundos
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
  });
}

// Hook legado - retorno simplificado com array de contratos
export function useContratos(limit: number = 200) {
  return useQuery({
    queryKey: ['contratos', 'default', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contratos')
        .select(`
          *,
          cliente:clientes(id, nome, email, telefone, cidade, estado),
          pedido:pedidos!pedido_id(id, numero_pedido, status),
          projeto:projetos!projeto_id(id, status)
        `)
        .order('id', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Erro ao buscar contratos (hook simples):', error);
        throw error;
      }

      return (data || []) as Contrato[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCriarContrato() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contratoData: {
      cliente_id: number;
      projeto_id?: number;
      pedido_id?: number;
      valor: number;
      data_inicio: string;
      data_fim?: string;
      observacoes?: string;
      arquivo_pdf_url?: string;
      arquivo_pdf_name?: string;
    }) => {
      let numeroCiclo = 1;
      let pedidoAnteriorId: number | null = null;
      if (contratoData.pedido_id) {
        const { data: pedido } = await supabase
          .from("pedidos")
          .select("numero_ciclo, tipo_negocio, pedido_anterior_id")
          .eq("id", contratoData.pedido_id)
          .maybeSingle();
        numeroCiclo = Number(pedido?.numero_ciclo) || (pedido?.tipo_negocio === "renovacao" ? 2 : 1);
        pedidoAnteriorId = pedido?.pedido_anterior_id ?? null;
      }

      if (contratoData.pedido_id && (numeroCiclo > 1 || pedidoAnteriorId)) {
        const existente = await findContratoDaCadeiaPedido(contratoData.pedido_id);
        if (existente?.id) {
          const { data: contratoAtualizado, error: errUpd } = await supabase
            .from("contratos")
            .update({
              pedido_id: contratoData.pedido_id,
              projeto_id: contratoData.projeto_id ?? null,
              valor: contratoData.valor,
              data_inicio: contratoData.data_inicio,
              data_fim: contratoData.data_fim ?? null,
              observacoes: contratoData.observacoes ?? null,
              numero_ciclo: numeroCiclo,
              status: "aguardando pagamento",
              seguro_ativado: false,
              primeira_parcela_paga: false,
              data_ativacao_seguro: null,
              data_primeira_parcela_paga: null,
              ...(contratoData.arquivo_pdf_url
                ? {
                    arquivo_pdf_url: contratoData.arquivo_pdf_url,
                    arquivo_pdf_name: contratoData.arquivo_pdf_name,
                  }
                : {}),
            })
            .eq("id", existente.id)
            .select("*")
            .single();

          if (errUpd) throw errUpd;
          return contratoAtualizado;
        }
      }

      // Gerar número do contrato sequencial baseado no ID
      const { data: ultimoContrato } = await supabase
        .from('contratos')
        .select('id')
        .order('id', { ascending: false })
        .limit(1)
        .single();
      
      const proximoId = (ultimoContrato?.id || 0) + 1;
      const numeroContrato = `CON${String(proximoId).padStart(6, '0')}`;

      const dadosParaInserir = {
        ...contratoData,
        numero_contrato: numeroContrato,
        numero_ciclo: numeroCiclo,
        status: 'aguardando pagamento'
      };

      console.log('Criando contrato:', dadosParaInserir);

      const { data: contratoCriado, error } = await supabase
        .from('contratos')
        .insert(dadosParaInserir)
        .select('*')
        .single();

      if (error) {
        console.error('Erro ao criar contrato:', error);
        throw error;
      }

      console.log('Contrato criado com sucesso:', contratoCriado);
      
      // Não criar evento de renovação de contrato na agenda
      // Apenas eventos de renovação de seguro são criados automaticamente
      
      return contratoCriado;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      // Disparar evento para atualizar estatísticas do dashboard
      window.dispatchEvent(new CustomEvent('contrato_criado', { detail: data }));
    }
  });
}

export function useAtualizarContrato() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: number; 
      status?: string;
      data_fim?: string;
      observacoes?: string;
      projeto_id?: number;
    }) => {
      const { data, error } = await supabase
        .from('contratos')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      // Disparar evento para atualizar estatísticas do dashboard
      window.dispatchEvent(new CustomEvent('contrato_atualizado', { detail: data }));
    }
  });
}
